import { GridColDef, GridCellParams } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getDonors } from 'src/shared/services/donorService';
import { formatPersonalNumber } from 'src/shared/functions/formatStringToDate';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box } from '@mui/material';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { DonorResponse } from '../models/donor';

type DonorDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchString?: string;
};

export const DonorDataGrid = (props: DonorDataGridProps) => {
  const { loadingCallback, searchString = undefined } = props;
  const { t } = useTranslation(['gift']);
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const [rows, setRows] = useState<DonorResponse[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    setTimeout(() => {
      scrollIntoView();
    }, 100);
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    loadingCallback(true);
    try {
      const result = await getDonors(data.page, data.pageSize, 'createdAt', 'desc', searchString);
      if (isMounted()) {
        const donorsResult = extendObjectWithId(result.data.results, 'id');
        ConsoleHelper.log('donorsResult', result.data.results);

        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(donorsResult);
        }, 100);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [data.page, data.pageSize, isMounted, loadingCallback, searchString]);

  useEffect(() => {
    fetchData();
  }, [data.page, data.pageSize, fetchData]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.DonorsDetail}/${params.row.id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.3,
    },
    {
      field: 'name',
      headerName: t('gift:donors'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{`${params.row.name}`}</>;
      },
    },
    {
      field: 'personalIdentityNumber',
      headerName: t('gift:detailDonor.personalIdentityNumber'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{formatPersonalNumber(params.row.personalIdentityNumber)}</>;
      },
    },
    {
      field: 'createdAt',
      headerName: t('gift:tableHeaders.created'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdAt)}</>;
      },
    },
  ];

  return (
    <Box ref={scrollRef}>
      <DataGrid<DonorResponse>
        columns={columns}
        rows={rows}
        autoHeight
        dataModel={data}
        checkboxSelection={false}
        onRowClick={onRowClick}
        disableSelectionOnClick
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={() => ConsoleHelper.log()}
        onSortModelChange={() => ConsoleHelper.log()}
        selectionModel={[]}
      />
    </Box>
  );
};
